import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { AssessmentService } from 'src/app/core/service/assessment.service';

/** Matches `${...}` runtime expression placeholders (capturing group for replace callbacks). */
export const EXPRESSION_PLACEHOLDER_PATTERN = /\$\{([^}]+)\}/g;

/** Decodes common HTML entity encodings of `$` from rich-text field definitions. */
export function normalizeInterpolationTemplate(template: string): string
{
  return (template ?? '')
    .replace(/&#0*36;/gi, '$')
    .replace(/&#x24;/gi, '$')
    .replace(/&dollar;/gi, '$');
}

/** Removes all `${...}` segments so unresolved expressions never appear in the UI. */
export function stripInterpolationPlaceholders(template: string): string
{
  const normalized = normalizeInterpolationTemplate(template);
  if (!normalized.includes('${'))
  {
    return normalized;
  }

  return normalized.replace(EXPRESSION_PLACEHOLDER_PATTERN, '');
}

export function normalizeFormlyFields(fields: any): FormlyFieldConfig[]
{
  if (Array.isArray(fields))
  {
    return fields;
  }

  if (fields && typeof fields === 'object')
  {
    return Object.values(fields) as FormlyFieldConfig[];
  }

  return [];
}

/**
 * Final safety pass: strips `${...}` from display strings on the field tree (labels, descriptions, templates, defaults).
 */
export function stripInterpolationPlaceholdersInFields(fields: FormlyFieldConfig[]): void
{
  const walk = (field: FormlyFieldConfig): void =>
  {
    field.fieldGroup?.forEach(walk);

    if (field.templateOptions)
    {
      for (const key of Object.keys(field.templateOptions))
      {
        const value = field.templateOptions[key];
        if (typeof value === 'string')
        {
          field.templateOptions[key] = stripInterpolationPlaceholders(value);
        }
      }

      if (field.type === 'label' && field.templateOptions.label && !field.templateOptions.description)
      {
        field.templateOptions.description = field.templateOptions.label;
      }

      if (field.type === 'label')
      {
        field.templateOptions.label = '';
        (field.templateOptions as { hideLabel?: boolean }).hideLabel = true;
      }
    }

    if (typeof field.template === 'string')
    {
      field.template = stripInterpolationPlaceholders(field.template);
    }

    if (typeof field.defaultValue === 'string')
    {
      field.defaultValue = stripInterpolationPlaceholders(field.defaultValue);
    }

    if (field.expressionProperties)
    {
      for (const key of Object.keys(field.expressionProperties))
      {
        const value = field.expressionProperties[key];
        if (typeof value === 'string')
        {
          field.expressionProperties[key] = stripInterpolationPlaceholders(value);
        }
      }
    }
  };

  normalizeFormlyFields(fields).forEach(walk);
}

/**
 * Sets formState.patient, resolves or blanks `${...}` on display strings and defaults, and refreshes Formly when patient is present.
 */
export function applyFormlyPatientContext(
  assessmentService: AssessmentService,
  fields: FormlyFieldConfig[],
  model: any,
  formlyOptions: FormlyFormOptions,
  patient: any
): void
{
  if (!fields?.length || !formlyOptions?.formState)
  {
    return;
  }

  formlyOptions.formState.patient = patient ?? null;

  assessmentService.setFormlyModelDefaults(fields, model);
  assessmentService.applyFormlyInterpolatedExpressions(fields, model, formlyOptions.formState);

  if (patient)
  {
    assessmentService.triggerFormlyExpressionRefresh(formlyOptions, fields);
    assessmentService.runAfterFormlyFieldsAttach(() =>
    {
      assessmentService.applyModelValuesToFormControls(fields, model);
    }, 2);
  }

  stripInterpolationPlaceholdersInFields(fields);
}
